import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Checkbox } from "@/components/ui/checkbox"
import { trackConversion, visitSucceeded } from "@/lib/analytics"
import { FormError, useHoneypot } from "@/components/public-form"
import type { PageProps } from "@/types/inertia"

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-display">{message}</p>
}

// The therapy enquiry form — shared by Gisela's home/contact pages and the
// legacy sanctuary therapy page. Posts to /therapy-enquiries, which
// redirect_backs to the submitting page; the fragment is restored manually
// because Inertia's fetch-based redirects drop it.
//
// `compact` is the short version that sits beside a photo rather than filling
// a band of its own: name, email, message. It asks nothing about language or
// format — those go in as "either" / "no preference", which is the truthful
// answer when the visitor was never asked, and Gisela settles them in her
// reply. It is otherwise the same enquiry, in the same inbox, firing the same
// conversion event.
export function EnquiryForm({ inPersonNote, compact = false }: { inPersonNote?: string; compact?: boolean }) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const tf = props.translations.therapy.enquiry_form
  const tg = props.translations.gisela_pages.enquiry

  const [name, setName] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [languagePref, setLanguagePref] = React.useState("")
  const [sessionFormat, setSessionFormat] = React.useState("online")
  const [inPersonLocation, setInPersonLocation] = React.useState("")
  const [openToOnline, setOpenToOnline] = React.useState(false)
  const [message, setMessage] = React.useState("")
  const [referralSource, setReferralSource] = React.useState("")
  const [processing, setProcessing] = React.useState(false)
  const honeypot = useHoneypot("tg-company")

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      "/therapy-enquiries",
      {
        therapy_enquiry: {
          name,
          email,
          language_pref: compact ? "Either" : languagePref,
          location_pref: compact
            ? "no_preference"
            : sessionFormat === "in_person"
              ? inPersonLocation
              : sessionFormat,
          // Only meaningful for in-person enquiries; null means "never asked".
          open_to_online: !compact && sessionFormat === "in_person" ? openToOnline : null,
          message,
          referral_source: referralSource,
        },
        ...honeypot.payload,
      },
      {
        onSuccess: (page) => {
          if (visitSucceeded(page)) trackConversion("enquiry")
        },
        onFinish: () => {
          setProcessing(false)
          window.location.hash = "enquiry-form"
        },
      }
    )
  }

  return (
    <form onSubmit={handleSubmit} className={compact ? "mt-6 space-y-4" : "mt-8 space-y-5"}>
      <FormError message={errors.base} />
      {honeypot.field}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="tg-name">{tf.name}</label>
          <Input
            id="tg-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={!!errors.name}
            className="mt-1"
            placeholder={tg.name_placeholder}
          />
          <FieldError message={errors.name} />
        </div>
        <div>
          <label htmlFor="tg-email">{tf.email}</label>
          <Input
            id="tg-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!errors.email}
            className="mt-1"
            placeholder={tg.email_placeholder}
          />
          <FieldError message={errors.email} />
        </div>
      </div>

      {!compact && (
        <>
          <div>
            <fieldset>
              <legend>{tf.language}</legend>
              <RadioGroup orientation="horizontal" className="mt-2">
                {[
                  { value: "English", label: tf.language_english },
                  { value: "Spanish", label: tf.language_spanish },
                  { value: "Either", label: tf.language_either },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 font-normal text-ink-body">
                    <Radio
                      name="language_pref"
                      value={value}
                      checked={languagePref === value}
                      onChange={() => setLanguagePref(value)}
                    />
                    {label}
                  </label>
                ))}
              </RadioGroup>
            </fieldset>
            <FieldError message={errors.language_pref} />
          </div>

          <div>
            <fieldset>
              <legend>{tf.format}</legend>
              <RadioGroup className="mt-2">
                {[
                  { value: "online", label: tf.format_online },
                  { value: "in_person", label: tf.format_in_person },
                  { value: "no_preference", label: tf.format_no_pref },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 font-normal text-ink-body">
                    <Radio
                      name="session_format"
                      value={value}
                      checked={sessionFormat === value}
                      onChange={() => setSessionFormat(value)}
                    />
                    {label}
                  </label>
                ))}
              </RadioGroup>
            </fieldset>

            {sessionFormat === "in_person" && (
              <div className="mt-3 space-y-4 rounded-xl bg-white p-4 ring-1 ring-hairline">
                {inPersonNote && <p className="text-sm text-ink-muted">{inPersonNote}</p>}
                <div>
                  <label htmlFor="tg-centre">{tf.centre}</label>
                  <Select
                    id="tg-centre"
                    required
                    value={inPersonLocation}
                    onChange={(e) => setInPersonLocation(e.target.value)}
                    aria-invalid={!!errors.location_pref}
                    className="mt-1"
                  >
                    <option value="">{tf.select_option}</option>
                    <option value="prestwich">{tf.centre_prestwich}</option>
                    <option value="hebden_bridge">{tf.centre_hebden}</option>
                    <option value="no_preference">{tf.centre_either}</option>
                  </Select>
                </div>
                <label className="flex items-start gap-2 font-normal text-ink-body">
                  <Checkbox
                    checked={openToOnline}
                    onChange={(e) => setOpenToOnline(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm">{tf.open_to_online}</span>
                </label>
              </div>
            )}
            <FieldError message={errors.location_pref} />
          </div>
        </>
      )}

      <div>
        <label htmlFor="tg-message">{tf.message}</label>
        {/* form-control alone is a one-line h-10 input, which clipped the
            placeholder and made the box the smallest field on the form — the
            design system's textarea sizing is the second class. */}
        <textarea
          id="tg-message"
          rows={compact ? 5 : 7}
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-invalid={!!errors.message}
          className="form-control form-control-textarea mt-1 w-full resize-y"
          placeholder={tf.message_placeholder}
        />
        <FieldError message={errors.message} />
      </div>

      {!compact && (
        <div>
          <label htmlFor="tg-referral">
            {tf.referral}{" "}
            <span className="text-ink-muted font-normal">{props.translations.common.optional}</span>
          </label>
          <Select
            id="tg-referral"
            value={referralSource}
            onChange={(e) => setReferralSource(e.target.value)}
            aria-invalid={!!errors.referral_source}
            className="mt-1"
          >
            <option value="">{tf.select_option}</option>
            <option value="google">{tf.referral_google}</option>
            <option value="word_of_mouth">{tf.referral_word_of_mouth}</option>
            <option value="psychology_today">{tf.referral_psychology_today}</option>
            <option value="other">{tf.referral_other}</option>
          </Select>
          <FieldError message={errors.referral_source} />
        </div>
      )}

      <div className="pt-2">
        <Button type="submit" variant="primary" size="lg" disabled={processing} className="w-full sm:w-auto">
          {tf.submit}
        </Button>
      </div>
    </form>
  )
}

// The full "Get in touch" section: heading, intro/thank-you state, and the form.
export function EnquiryFormSection({ inPersonNote }: { inPersonNote?: string }) {
  const { props } = usePage<PageProps>()
  const notice = props.flash.notice
  const tf = props.translations.therapy.enquiry_form

  return (
    <section id="enquiry-form" className="bg-cream py-16">
      <div className="mx-auto max-w-2xl px-6 lg:px-8">
        <h2>{props.translations.gisela_pages.enquiry.heading}</h2>

        {notice ? (
          <div className="mt-8 rounded-2xl bg-white p-8 text-center ring-1 ring-hairline">
            <h3>{tf.thank_you}</h3>
            <p className="mt-3 text-ink-muted">{notice}</p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-ink-muted">{tf.intro}</p>
            <EnquiryForm inPersonNote={inPersonNote} />
          </>
        )}
      </div>
    </section>
  )
}
